import { useMemo, useState } from "react";
import { KPI_CATALOG, type KpiCatalogEntry } from "../lib/kpiCatalog";

type ImpactFilter = "all" | "Critical" | "High" | "Medium" | "Low";
type TypeFilter = "all" | "KPI" | "KRI";

const IMPACT_FILTERS: Array<{ id: ImpactFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "Critical", label: "Critical" },
  { id: "High", label: "High" },
  { id: "Medium", label: "Medium" },
  { id: "Low", label: "Low" },
];

const TYPE_FILTERS: Array<{ id: TypeFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "KPI", label: "KPI" },
  { id: "KRI", label: "KRI" },
];

function groupByCategory(rows: KpiCatalogEntry[]): Map<string, KpiCatalogEntry[]> {
  const map = new Map<string, KpiCatalogEntry[]>();
  for (const r of rows) {
    const list = map.get(r.category) ?? [];
    list.push(r);
    map.set(r.category, list);
  }
  return map;
}

export default function KpiReference() {
  const [query, setQuery] = useState("");
  const [impact, setImpact] = useState<ImpactFilter>("all");
  const [typeF, setTypeF] = useState<TypeFilter>("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return KPI_CATALOG.filter((r) => {
      if (impact !== "all" && r.impact !== impact) return false;
      if (typeF !== "all" && r.type !== typeF) return false;
      if (!q) return true;
      const hay = `${r.id} ${r.name} ${r.category} ${r.summary} ${r.formula} ${r.where} ${r.why}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, impact, typeF]);

  const grouped = useMemo(() => groupByCategory(filtered), [filtered]);

  const counts = useMemo(() => ({
    total: KPI_CATALOG.length,
    critical: KPI_CATALOG.filter((r) => r.impact === "Critical").length,
    high: KPI_CATALOG.filter((r) => r.impact === "High").length,
    medium: KPI_CATALOG.filter((r) => r.impact === "Medium").length,
    low: KPI_CATALOG.filter((r) => r.impact === "Low").length,
  }), []);

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">
            KPI Reference <span className="page-pill">{KPI_CATALOG.length} INDICATORS</span>
          </h1>
          <p className="page-sub">
            Every metric, risk indicator, and threshold the dashboard surfaces — explained.
            Each entry shows the formula (where applicable), where it appears, why it matters,
            and how it drives action. Filter by impact tier, type, or search by ID, name, or category.
          </p>
        </div>
      </div>

      <div className="kpiref-summary">
        <div className="kpiref-summary-tile critical">
          <div className="kpiref-summary-val">{counts.critical}</div>
          <div className="kpiref-summary-lbl">Critical</div>
        </div>
        <div className="kpiref-summary-tile high">
          <div className="kpiref-summary-val">{counts.high}</div>
          <div className="kpiref-summary-lbl">High</div>
        </div>
        <div className="kpiref-summary-tile medium">
          <div className="kpiref-summary-val">{counts.medium}</div>
          <div className="kpiref-summary-lbl">Medium</div>
        </div>
        <div className="kpiref-summary-tile low">
          <div className="kpiref-summary-val">{counts.low}</div>
          <div className="kpiref-summary-lbl">Low</div>
        </div>
        <div className="kpiref-summary-tile total">
          <div className="kpiref-summary-val">{counts.total}</div>
          <div className="kpiref-summary-lbl">Total</div>
        </div>
      </div>

      <div className="kpiref-toolbar">
        <input
          type="search"
          className="kpiref-search"
          placeholder="Search by K-ID, name, category, formula…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="kpiref-pill-group">
          <span className="kpiref-pill-label">Type</span>
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`kpiref-pill${typeF === f.id ? " active" : ""}`}
              onClick={() => setTypeF(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="kpiref-pill-group">
          <span className="kpiref-pill-label">Impact</span>
          {IMPACT_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`kpiref-pill${impact === f.id ? " active" : ""}`}
              onClick={() => setImpact(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="kpiref-empty">No entries match the current filter.</div>
      ) : (
        <div className="kpiref-results-meta">
          Showing <strong>{filtered.length}</strong> of {KPI_CATALOG.length}
        </div>
      )}

      {Array.from(grouped.entries()).map(([cat, rows]) => (
        <section key={cat} className="kpiref-section">
          <h2 className="kpiref-section-title">{cat} <span className="kpiref-section-count">{rows.length}</span></h2>
          <div className="kpiref-grid">
            {rows.map((r) => (
              <article key={r.id} className={`kpiref-card impact-${r.impact.toLowerCase()}`}>
                <header className="kpiref-card-head">
                  <span className="kpiref-card-id">{r.id}</span>
                  <span className={`kpiref-card-type type-${r.type.toLowerCase()}`}>{r.type}</span>
                  <span className={`kpiref-card-impact impact-${r.impact.toLowerCase()}`}>{r.impact}</span>
                </header>
                <h3 className="kpiref-card-name">{r.name}</h3>
                <p className="kpiref-card-summary">{r.summary}</p>

                <dl className="kpiref-card-detail">
                  <dt>Formula</dt>
                  <dd className="kpiref-formula">{r.formula}</dd>
                  <dt>Where</dt>
                  <dd>{r.where}</dd>
                  <dt>Why</dt>
                  <dd>{r.why}</dd>
                  <dt>How</dt>
                  <dd>{r.how}</dd>
                </dl>

                <footer className="kpiref-card-foot">
                  <span className="kpiref-card-meta">{r.owner}</span>
                  <span className="kpiref-card-meta">· {r.refresh}</span>
                </footer>
              </article>
            ))}
          </div>
        </section>
      ))}
    </section>
  );
}
